<template>
  <v-dialog
    :value="show"
    width="700"
    :scrollable="false"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="ma-0 pa-0">
      <v-card-title>
        {{ $t('common.site_other') }}
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pb-0 mb-3">
        <v-row class="ma-0 pt-3">
          <v-col cols="6">
            <div class="d-flex justify-space-between align-end">
              <span
                class="subtitle-2 font-weight-medium blue-grey--text text--darken-3"
              >
                {{ $t('common.site_other') }}
              </span>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="d-flex justify-space-between align-end">
              <span
                v-if="hasSitesSelected"
                class="subtitle-2 font-weight-medium blue-grey--text text--darken-3"
              >
                {{ selectedText }}
              </span>
              <span
                v-if="hasSitesSelected"
                class="cursor-pointer primary--text"
                @click="onClearAll"
              >
                {{ $t('common.clear_all') }}
              </span>
            </div>
          </v-col>
        </v-row>

        <v-row class="ma-0 pa-0">
          <v-col cols="6">
            <v-sheet
              rounded
              outlined
              class="mt-4 sheet"
              data-tourid="advanced-sites-selector-unselected-sites"
            >
              <v-text-field
                v-model="searchTerm"
                dense
                clearable
                hide-details
                class="site-search-bar pa-2 mt-0"
                prepend-inner-icon="mdi-magnify"
                :disabled="isLoading"
                :placeholder="$t('common.search')"
              />

              <v-list dense color="transparent" class="pa-1">
                <div
                  v-if="isLoading && !loadingMoreItems"
                  class="search-advanced-skeletons"
                >
                  <v-skeleton-loader
                    type="list-item-avatar-two-line,
											list-item-avatar-two-line,
											list-item-avatar-two-line,
											list-item-avatar-two-line,
											list-item-avatar-two-line,
											list-item-avatar-two-line,
											list-item-avatar-two-line"
                  />
                </div>
                <div v-else>
                  <div v-for="item in unselectedSites" :key="item.id">
                    <SiteListItem
                      class="pl-1 pr-3 py-0"
                      :site="item"
                      :searchTerm="searchTerm"
                      @click="onSelect(item)"
                    />
                  </div>

                  <div class="d-flex justify-center">
                    <Button
                      v-if="moreResults"
                      plain
                      :text="$t('common.load_more')"
                      :loading="loadingMoreItems"
                      :disabled="loadingMoreItems"
                      @click="getMoreItems"
                    />
                  </div>
                </div>
              </v-list>
            </v-sheet>
          </v-col>
          <v-col cols="6">
            <v-sheet
              rounded
              outlined
              class="sheet mt-4"
              data-tourid="advanced-sites-selector-selected-sites"
              :class="{
                'd-flex align-center justify-center': !hasSitesSelected,
              }"
            >
              <div v-if="hasSitesSelected">
                <v-text-field
                  v-model="localSearchTerm"
                  dense
                  clearable
                  hide-details
                  class="site-search-bar pa-2 mt-0"
                  prepend-inner-icon="mdi-magnify"
                  :placeholder="$t('common.search')"
                />

                <v-list dense color="transparent" class="pa-0 ma-0">
                  <div v-for="item in selectedSites" :key="item.id">
                    <SiteListItem
                      class="pl-1 pr-3 py-0"
                      :site="item"
                      :searchTerm="localSearchTerm"
                      @click="onRemove(item)"
                    />
                  </div>
                </v-list>
              </div>

              <div
                v-if="hasSelectedButNoResults"
                class="d-flex align-center justify-center"
                style="height: calc(100% - 70px)"
              >
                <EmptyState
                  class="px-6"
                  icon="mdi-ghost-outline"
                  :iconSize="32"
                  :title="$t('common.no_results_found')"
                  :message="$t('common.no_matches_text')"
                />
              </div>

              <EmptyState
                v-if="!hasSitesSelected"
                icon="mdi-warehouse"
                :title="$t('common.site_other')"
                :message="$t('common.no_results_found')"
              />
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-end py-4">
        <Button
          plain
          color="grey"
          class="me-6"
          :text="$t('common.button.cancel')"
          @click="handleClose"
        />
        <Button
          plain
          class="me-5"
          color="primary"
          :text="$t('common.select')"
          @click="handleSelect"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { fuseSearch } from '@/lib/fuseSearch'

import Button from '@/components/UI/Button.vue'
import EmptyState from '@/components/EmptyState.vue'

import SiteListItem from './SiteListItem.vue'

const ITEMS_PER_PAGE = 40

export default {
  name: 'AdvancedSitesSelectorModal',

  mixins: [mixin],

  components: {
    Button,
    EmptyState,
    SiteListItem,
  },

  props: {
    show: Boolean,
    initialSites: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    maxSelectableSites: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      searchTerm: '',
      localSearchTerm: '',
      loadPage: 0,
      isLoading: false,
      loadingMoreItems: false,
      moreResults: false,
      availableSites: [],
      cachedSites: {},
      selectedSiteIds: [],
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'organizationSites']),

    organizationSitesMap() {
      return new Map(
        (this.organizationSites || []).map(site => [
          site?.id ?? site?._id,
          this.normalizeSite(site),
        ])
      )
    },

    normalizedInitialSites() {
      return this.parseSites(this.initialSites)
    },

    selectedSites() {
      const selected = _.compact(
        this.selectedSiteIds.map(
          id => this.cachedSites[id] ?? this.organizationSitesMap.get(id)
        )
      )

      if (!this.localSearchTerm) {
        return _.orderBy(
          selected,
          item => item.title?.toLowerCase?.() ?? '',
          'asc'
        )
      }

      return fuseSearch(selected, ['title'], this.localSearchTerm)
    },

    unselectedSites() {
      const selectedSet = new Set(this.selectedSiteIds)
      return this.availableSites.filter(site => !selectedSet.has(site.id))
    },

    hasSitesSelected() {
      return Boolean(this.selectedSiteIds.length)
    },

    hasSelectedButNoResults() {
      return this.hasSitesSelected && !this.selectedSites.length
    },

    selectedText() {
      return `${this.$t('common.selected')} (${this.selectedSiteIds.length})`
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.initializeState()
        }
      },
    },

    initialSites: {
      immediate: true,
      handler(sites) {
        this.seedSelectedSites(sites)
      },
    },

    searchTerm: {
      handler: _.debounce(function(term) {
        if (!this.show) return
        this.searchItems(term)
      }, 400),
    },
  },

  methods: {
    normalizeSite(site) {
      if (!site) return null
      const id = site?._id ?? site?.id
      if (!id) return null

      return {
        ...site,
        id,
        _id: id,
        title: site?.title ?? site?.name ?? '',
        subtitle:
          site?.subtitle ??
          (site?.countryCodes?.length
            ? this.$t(`countries.${site.countryCodes[0]}`)
            : site?.code ?? ''),
      }
    },

    parseSites(sites = []) {
      return _.compact(sites?.map?.(site => this.normalizeSite(site)))
    },

    seedSelectedSites(sites = []) {
      const parsed = this.parseSites(sites)

      if (!parsed.length) {
        this.selectedSiteIds = []
        return
      }

      this.selectedSiteIds = _.uniq(parsed.map(site => site.id))
      this.addSitesToCache(parsed)
    },

    initializeState() {
      this.searchTerm = ''
      this.localSearchTerm = ''
      this.loadPage = 0
      this.availableSites = []
      this.moreResults = false
      this.addSitesToCache(this.normalizedInitialSites)
      this.selectedSiteIds = _.uniq(
        this.normalizedInitialSites.map(site => site.id)
      )
      this.searchItems()
    },

    async searchItems(terms = '') {
      if (!this.show) return
      try {
        this.isLoading = true
        this.loadPage = 0

        const response = await api.site.getStreamSites(this.organizationId, {
          filters: this.filters,
          limit: ITEMS_PER_PAGE,
          from: 0,
          terms,
          sort: {
            by: terms?.length ? '_score' : 'name',
            order: terms?.length ? 'desc' : 'asc',
          },
        })

        const results =
          response?.data?.results?.map(result => result.site) ?? []
        const parsed = this.parseSites(results)
        this.availableSites = parsed
        this.addSitesToCache(parsed)

        const total = response?.data?.totalRecordsInData ?? parsed.length
        this.moreResults = parsed.length < total
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },

    async getMoreItems() {
      if (this.loadingMoreItems || !this.moreResults) return

      try {
        this.loadingMoreItems = true
        this.loadPage += 1

        const response = await api.site.getStreamSites(this.organizationId, {
          filters: this.filters,
          limit: ITEMS_PER_PAGE,
          from: ITEMS_PER_PAGE * this.loadPage,
          terms: this.searchTerm,
          sort: {
            by: this.searchTerm?.length ? '_score' : 'name',
            order: this.searchTerm?.length ? 'desc' : 'asc',
          },
        })

        const results =
          response?.data?.results?.map(result => result.site) ?? []
        const parsed = this.parseSites(results)
        this.availableSites = _.uniqBy(
          [...this.availableSites, ...parsed],
          'id'
        )
        this.addSitesToCache(parsed)

        const total = response?.data?.totalRecordsInData ?? 0
        this.moreResults = this.availableSites.length < total
      } catch (error) {
        this.showError(error)
      } finally {
        this.loadingMoreItems = false
      }
    },

    onSelect(site) {
      const normalizedSite = this.normalizeSite(site)
      if (!normalizedSite) return

      const nextIds = _.uniq([...this.selectedSiteIds, normalizedSite.id])

      if (this.maxSelectableSites && nextIds.length > this.maxSelectableSites) {
        this.showError(
          this.$t('common.selection_limit_error', {
            selectionLimit: this.maxSelectableSites,
            type: this.$t('common.site_other'),
          })
        )
        return
      }

      this.selectedSiteIds = nextIds
      this.addSitesToCache([normalizedSite])
    },

    onRemove(site) {
      const id = site?.id ?? site?._id
      if (!id) return

      this.selectedSiteIds = this.selectedSiteIds.filter(
        siteId => siteId !== id
      )
    },

    onClearAll() {
      this.selectedSiteIds = []
    },

    handleSelect() {
      const items = this.selectedSiteIds
        .map(id => this.cachedSites[id] ?? this.organizationSitesMap.get(id))
        .filter(Boolean)

      this.$emit('select', {
        ids: this.selectedSiteIds,
        items,
      })

      this.handleClose()
    },

    handleClose() {
      this.$emit('close')
    },

    addSitesToCache(sites = []) {
      const parsed = this.parseSites(sites)
      if (!parsed.length) return

      this.cachedSites = { ...this.cachedSites, ..._.keyBy(parsed, 'id') }
      this.$emit('cacheUpdate', parsed)
    },
  },
}
</script>

<style scoped lang="scss">
.sheet {
  min-height: 360px;
}

.site-search-bar {
  font-size: 12px !important;

  .v-input__slot::after,
  .v-input__slot::before {
    border-style: none !important;
  }

  .v-input__slot .v-text-field__slot input {
    padding-top: 0;
  }
}

.search-advanced-skeletons
  .v-skeleton-loader
  .v-skeleton-loader__list-item-avatar-two-line {
  height: 40px;
  margin: 4px 0;

  .v-skeleton-loader__avatar {
    margin-left: 4px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
}
</style>
